import React from 'react';
import Textarea from '../../../components/ui/Textarea';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const SchemaPreview = ({ criteria }) => {
  const { t } = useTranslation('reviews');
  const renderQuestionPreview = (question) => {
    switch (question?.questionType) {
      case 'scale':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{question?.scaleMinLabel || question?.scaleMin}</span>
              <span>{question?.scaleMaxLabel || question?.scaleMax}</span>
            </div>
            <input
              type="range"
              min={question?.scaleMin || 1}
              max={question?.scaleMax || 5}
              defaultValue={Math.floor(((question?.scaleMin || 1) + (question?.scaleMax || 5)) / 2)}
              className="w-full"
              disabled
            />
            <div className="flex justify-center gap-2">
              {Array.from({ length: (question?.scaleMax || 5) - (question?.scaleMin || 1) + 1 }, (_, i) => (
                <button
                  key={i}
                  className="w-10 h-10 rounded-lg border border-border hover:border-primary hover:bg-primary/10 transition-colors duration-250 text-sm font-medium"
                  disabled
                >
                  {(question?.scaleMin || 1) + i}
                </button>
              ))}
            </div>
          </div>
        );

      case 'true_false':
        return (
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name={`preview-${question?.id}`} disabled />
              <span className="text-foreground">{t('schemaBuilder.preview.true')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name={`preview-${question?.id}`} disabled />
              <span className="text-foreground">{t('schemaBuilder.preview.false')}</span>
            </label>
          </div>
        );

      case 'short_text':
        return (
          <input
            type="text"
            placeholder={t('schemaBuilder.preview.shortTextPlaceholder')}
            className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            disabled
          />
        );

      case 'long_text':
        return (
          <Textarea
            placeholder={t('schemaBuilder.preview.longTextPlaceholder')}
            rows={4}
                        disabled
          />
        );

      default:
        return null;
    }
  };

  if (criteria?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="Eye" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('schemaBuilder.preview.emptyTitle')}</h3>
        <p className="text-muted-foreground">
          {t('schemaBuilder.preview.emptySubtitle')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-muted/40 rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="Eye" size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-foreground">{t('schemaBuilder.preview.headerTitle')}</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('schemaBuilder.preview.headerSubtitle')}
        </p>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">{t('schemaBuilder.preview.formTitle')}</h3>
        
        <div className="space-y-6">
          {criteria?.map((question, index) => (
            <div key={question?.id} className="pb-6 border-b border-border last:border-b-0 last:pb-0">
              <div className="mb-3">
                <label className="block text-sm font-medium text-foreground mb-1">
                  {index + 1}. {question?.questionText}
                  {question?.isRequired && <span className="text-error ml-1">*</span>}
                </label>
                {question?.helpText && (
                  <p className="text-xs text-muted-foreground">{question?.helpText}</p>
                )}
              </div>
              {renderQuestionPreview(question)}
            </div>
          ))}

          <div className="pb-6 border-b border-border">
            <div className="mb-3">
              <label className="block text-sm font-medium text-foreground mb-1">
                {criteria?.length + 1}. {t('schemaBuilder.preview.generalComments')}
                <span className="text-error ml-1">*</span>
              </label>
              <p className="text-xs text-muted-foreground">
                {t('schemaBuilder.preview.generalCommentsHelp')}
              </p>
            </div>
            <Textarea
              placeholder={t('schemaBuilder.preview.mandatoryPlaceholder')}
              rows={6}
                            disabled
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors duration-250"
              disabled
            >
              {t('schemaBuilder.preview.saveDraft')}
            </button>
            <button
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-250"
              disabled
            >
              {t('schemaBuilder.preview.submitReview')}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">{t('schemaBuilder.preview.noteTitle')}</p>
            <p>{t('schemaBuilder.preview.noteBody')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchemaPreview;